/**
 * BoundingBoxOverlay Component
 * Overlays bounding boxes on video player for V4 target detection visualization
 * 
 * Features:
 * - Shows detection region when timestamp is clicked
 * - Smooth fade-in/fade-out animations
 * - Confidence-based coloring
 * - Auto-hides after timeout
 */

import React, { useState, useEffect } from 'react';
import '../../styles/v4-bounding-box.css';

const BoundingBoxOverlay = ({ 
  boundingBox = null,
  targetType = 'unknown',
  targetLabel = '',
  videoElement = null,
  autoHideDelay = 3000 // Auto-hide after 3 seconds
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Update dimensions when video element changes
  useEffect(() => {
    if (!videoElement) return;

    const updateDimensions = () => {
      setDimensions({
        width: videoElement.clientWidth,
        height: videoElement.clientHeight
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => window.removeEventListener('resize', updateDimensions);
  }, [videoElement]);

  // Show/hide bounding box when it changes
  useEffect(() => {
    if (boundingBox) {
      setIsVisible(true);
      
      // Auto-hide after delay
      if (autoHideDelay > 0) {
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, autoHideDelay);
        
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [boundingBox, autoHideDelay]);

  // Don't render if no bounding box or video
  if (!boundingBox || !videoElement || !isVisible) {
    return null;
  }

  // Calculate box position and size
  // Bounding box format: { x, y, width, height, confidence }
  // Coordinates are normalized (0-1) relative to video dimensions
  const boxStyle = {
    left: `${boundingBox.x * 100}%`,
    top: `${boundingBox.y * 100}%`,
    width: `${boundingBox.width * 100}%`,
    height: `${boundingBox.height * 100}%`
  };

  // Color based on confidence
  const getBoxColor = (confidence) => {
    if (confidence >= 0.9) return '#22c55e'; // Green - high confidence
    if (confidence >= 0.7) return '#eab308'; // Yellow - medium confidence
    return '#ef4444'; // Red - low confidence
  };

  const boxColor = getBoxColor(boundingBox.confidence || 0.9);

  // Icon based on target type
  const getIcon = (type) => {
    const icons = {
      vehicle: '◆',
      person: '●',
      environment: '▲',
      videographer: '■',
      text: '⊞',
      weapon: '▼',
      object: '◇'
    };
    return icons[type] || '◎';
  };

  return (
    <div className="v4-bbox-overlay-container">
      {/* Bounding Box */}
      <div 
        className="v4-bbox"
        style={{
          ...boxStyle,
          borderColor: boxColor,
          boxShadow: `0 0 0 2px ${boxColor}40, 0 0 20px ${boxColor}80`
        }}
      >
        {/* Label */}
        <div 
          className="v4-bbox-label"
          style={{
            background: boxColor,
            color: '#fff'
          }}
        >
          <span className="v4-bbox-icon">{getIcon(targetType)}</span>
          <span className="v4-bbox-text">
            {targetLabel}
            {boundingBox.confidence && (
              <span className="v4-bbox-confidence">
                {Math.round(boundingBox.confidence * 100)}%
              </span>
            )}
          </span>
        </div>

        {/* Corner markers */}
        <div className="v4-bbox-corner v4-bbox-corner-tl" style={{ borderColor: boxColor }}></div>
        <div className="v4-bbox-corner v4-bbox-corner-tr" style={{ borderColor: boxColor }}></div>
        <div className="v4-bbox-corner v4-bbox-corner-bl" style={{ borderColor: boxColor }}></div>
        <div className="v4-bbox-corner v4-bbox-corner-br" style={{ borderColor: boxColor }}></div>
      </div>

      {/* Dimmed overlay outside the box */}
      <div className="v4-bbox-dim"></div>
    </div>
  );
};

export default BoundingBoxOverlay;

